import { useEffect, useMemo, useState } from 'react';
import { Download, ExternalLink, Search, Table2, X } from 'lucide-react';
import type { CustomFieldType, CustomListRow } from '@contracts';
import { exportExcel } from '@/lib/exportData';
import { canOpenOdooLinks } from '@/services/authService';
import { ODOO_WEB_URL } from '@/lib/config';

export interface CustomDrillColumn {
  field: string;
  label: string;
  type: CustomFieldType;
  /** Se pinta como enlace al registro en Odoo (folio). */
  link?: boolean;
}

const recordUrl = (model: string, id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=${model}&view_type=form`;

const MONEY_HINT = /amount|total|price|paid|pending|subtotal|discount|balance|cost/i;
const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const isMoney = (c: CustomDrillColumn) => c.type === 'number' && MONEY_HINT.test(c.field);
const isNumeric = (c: CustomDrillColumn) => c.type === 'number';
/** Color del monto según el rol del campo (espeja el drill de apartados). */
const moneyClass = (field: string) =>
  /paid|abonado/i.test(field)
    ? 'text-emerald-700 font-semibold'
    : /pending|cobrar/i.test(field)
      ? 'text-amber-700'
      : /amount_total|(^|_)total/i.test(field)
        ? 'font-bold text-slate-700'
        : 'text-slate-600';

/** Estado → etiqueta de color (mismos valores Odoo que el drill de apartados). */
const STATE_BADGE: Record<string, string> = {
  active: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  done: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-slate-200 text-slate-600',
  expired: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-rose-100 text-rose-700',
  cancel: 'bg-rose-100 text-rose-700',
};

function renderCell(col: CustomDrillColumn, value: CustomListRow[string]) {
  if (value === null || value === undefined || value === '') return <span className="text-slate-300">—</span>;
  if (col.type === 'selection') {
    const cls = STATE_BADGE[String(value)] ?? 'bg-slate-100 text-slate-600';
    return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{String(value)}</span>;
  }
  if (col.type === 'number') {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return isMoney(col) ? money(n) : n.toLocaleString('es-MX');
  }
  if (col.type === 'datetime' || col.type === 'date') return fmtDate(String(value));
  if (col.type === 'bool') return value ? 'Sí' : 'No';
  return String(value);
}

const cellRaw = (col: CustomDrillColumn, value: CustomListRow[string]) =>
  col.type === 'number' ? Number(value ?? 0) : value === null || value === undefined ? '' : String(value);
const PAGE_OPTS = [10, 50, 100, 0];

export interface CustomDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  columns: CustomDrillColumn[];
  rows: CustomListRow[];
  loading?: boolean;
  /** Columna por la que se agrupa (encabezado de grupo + subtotales). */
  groupBy?: string;
  /** Modelo Odoo de las filas → para el enlace de las columnas `link` (folio). */
  model?: string;
  warning?: string;
}

/** Drill-down de una métrica custom con el diseño del drill de apartados: agrupable,
 *  con subtotales, totales al pie y badges — pero genérico (columnas configurables). */
export function CustomDrilldownModal({ open, onClose, title, columns, rows, loading, groupBy, model, warning }: CustomDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [q, sortField, sortDir, pageSize, rows]);

  const moneyCols = useMemo(() => columns.filter(isMoney), [columns]);
  const groupCol = columns.find((c) => c.field === groupBy);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = term
      ? rows.filter((r) => columns.some((c) => String(r[c.field] ?? '').toLowerCase().includes(term)))
      : rows;
    const sortCol = sortField ? columns.find((c) => c.field === sortField) : null;
    if (sortCol || groupCol) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const cmp = (col: CustomDrillColumn, a: CustomListRow, b: CustomListRow, d: number) => {
        const av = cellRaw(col, a[col.field]);
        const bv = cellRaw(col, b[col.field]);
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * d;
        return String(av).localeCompare(String(bv)) * d;
      };
      out = [...out].sort((a, b) => {
        // Grupo primero (filas contiguas por grupo); luego la columna ordenada.
        if (groupCol) {
          const g = cmp(groupCol, a, b, 1);
          if (g !== 0) return g;
        }
        return sortCol ? cmp(sortCol, a, b, dir) : 0;
      });
    }
    return out;
  }, [rows, columns, q, sortField, sortDir, groupCol]);

  const sums = useMemo(
    () => Object.fromEntries(moneyCols.map((c) => [c.field, filtered.reduce((s, r) => s + Number(r[c.field] ?? 0), 0)])),
    [filtered, moneyCols],
  );

  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const pageRows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  // Subtotales por grupo (sobre lo filtrado, para el encabezado de cada grupo).
  const groupStats = useMemo(() => {
    if (!groupCol) return {};
    const m: Record<string, { count: number; sums: Record<string, number> }> = {};
    for (const r of filtered) {
      const k = String(r[groupCol.field] ?? '—');
      const g = (m[k] ??= { count: 0, sums: Object.fromEntries(moneyCols.map((c) => [c.field, 0])) });
      g.count++;
      for (const c of moneyCols) g.sums[c.field] += Number(r[c.field] ?? 0);
    }
    return m;
  }, [filtered, groupCol, moneyCols]);

  const toggleSort = (field: string, type: CustomFieldType) => {
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir(type === 'number' || type === 'datetime' || type === 'date' ? 'desc' : 'asc');
    }
  };

  const doExport = () =>
    void exportExcel('detalle', filtered.map((r) => Object.fromEntries(columns.map((c) => [c.label, cellRaw(c, r[c.field])]))));

  if (!open) return null;
  const arrow = (field: string) => (sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const groupSub = (g: { count: number; sums: Record<string, number> }) =>
    `${g.count} registros` + moneyCols.map((c) => ` · ${c.label} ${money(g.sums[c.field])}`).join('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-4 bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-600 px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15">
            <Table2 className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{rows.length}</span> registros
              {moneyCols.map((c) => (
                <span key={c.field}>{` · ${c.label} `}<span className="font-semibold text-white">{money(sums[c.field] ?? 0)}</span></span>
              ))}
            </p>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {warning && <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs font-medium text-amber-700">{warning}</div>}

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-fuchsia-400" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
            <div className="flex gap-1">
              {PAGE_OPTS.map((p) => (
                <button key={p} onClick={() => setPageSize(p)} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${pageSize === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {p === 0 ? 'Todos' : p}
                </button>
              ))}
            </div>
          </div>
          <button onClick={doExport} title="Descargar Excel" className="flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600">
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando detalle…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin registros para los filtros actuales.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  {columns.map((c) => (
                    <th key={c.field} onClick={() => toggleSort(c.field, c.type)} className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-fuchsia-600 ${isNumeric(c) ? 'text-right' : ''}`}>
                      {c.label}
                      <span className="ml-0.5 inline-block w-2 text-fuchsia-600">{arrow(c.field)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => {
                  const showGroup = groupCol && (i === 0 || r[groupCol.field] !== pageRows[i - 1][groupCol.field]);
                  const gKey = groupCol ? String(r[groupCol.field] ?? '—') : '';
                  return [
                    showGroup ? (
                      <tr key={`${gKey}-g`} className="select-none">
                        <td colSpan={columns.length} className="border-y border-fuchsia-200 bg-fuchsia-50/70 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-fuchsia-700">
                          <span className="mr-2 inline-block rounded bg-fuchsia-100 px-1.5 py-0.5 text-[10px] text-fuchsia-700">{gKey}</span>
                          {groupStats[gKey] ? groupSub(groupStats[gKey]) : ''}
                        </td>
                      </tr>
                    ) : null,
                    <tr key={(r.id as number | undefined) ?? i} className="border-b border-slate-100 hover:bg-slate-50">
                      {columns.map((c) => (
                        <td key={c.field} className={`px-4 py-2.5 ${isNumeric(c) ? `text-right tabular-nums ${isMoney(c) ? moneyClass(c.field) : ''}` : ''}`}>
                          {c.link && model && r.id != null && r[c.field] && canOpenOdooLinks() ? (
                            <a
                              href={recordUrl(model, Number(r.id))}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group inline-flex items-center gap-1 font-mono text-xs font-semibold text-fuchsia-700 hover:underline"
                            >
                              {String(r[c.field])}
                              <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                            </a>
                          ) : (
                            renderCell(c, r[c.field])
                          )}
                        </td>
                      ))}
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          {moneyCols.map((c) => (
            <span key={c.field} className="text-slate-600">
              {c.label} ({filtered.length}): <span className={`font-bold ${moneyClass(c.field)}`}>{money(sums[c.field] ?? 0)}</span>
            </span>
          ))}
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">{pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length}`}</span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">‹</button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
